/**
 * CSV compartido (27/10/2026): parser RFC 4180 (comillas, comas dentro de comillas, saltos de línea
 * dentro de un campo) con separador autodetectado (`,` `;` `\t`) y mapeo de cabeceras con alias en
 * español/inglés. Puro, sin I/O. Nació como tercer consumidor (alta masiva del Org Service) después
 * de `parseCsv` de shared/ta-metrics.ts y `parseVaultCsv` del ImportDialog del Vault (deuda anotada
 * ahí): los dos migran acá cuando se toquen.
 */
import { foldKey } from './text-normalize';

/** Separador más probable mirando la primera línea no vacía (coma por defecto). */
export function detectCsvSeparator(text: string): ',' | ';' | '\t' {
  const first = text.split(/\r?\n/).find((l) => l.trim()) ?? '';
  const counts: Array<[',' | ';' | '\t', number]> = [[',', 0], [';', 0], ['\t', 0]];
  let inQuotes = false;
  for (const ch of first) {
    if (ch === '"') inQuotes = !inQuotes;
    else if (!inQuotes) for (const c of counts) if (ch === c[0]) c[1]++;
  }
  counts.sort((a, b) => b[1] - a[1]);
  return counts[0][1] > 0 ? counts[0][0] : ',';
}

/** Filas × celdas. Comillas dobles escapadas como `""`. Las filas vacías se descartan. */
export function parseCsvRows(text: string, separator?: ',' | ';' | '\t'): string[][] {
  const sep = separator ?? detectCsvSeparator(text);
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  const src = text.replace(/^﻿/, '');
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') { cell += '"'; i++; } else inQuotes = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"') { inQuotes = true; continue; }
    if (ch === sep) { row.push(cell); cell = ''; continue; }
    if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(cell); cell = '';
      if (row.some((c) => c.trim())) rows.push(row);
      row = [];
      continue;
    }
    cell += ch;
  }
  if (inQuotes) throw new Error('CSV inválido: hay una comilla sin cerrar');
  row.push(cell);
  if (row.some((c) => c.trim())) rows.push(row);
  return rows.map((r) => r.map((c) => c.trim()));
}

const normHeader = (h: string) => foldKey(h).replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

export interface CsvTable<K extends string> {
  /** Cabecera original → campo; null si ninguna alias calzó. */
  headers: Array<{ raw: string; field: K | null }>;
  rows: Array<Partial<Record<K, string>>>;
  /** Campos requeridos que no están en la cabecera. */
  missing: K[];
}

/**
 * Tabla con cabecera: cada campo acepta varias cabeceras (alias, sin tildes ni mayúsculas, `_` por
 * cualquier separador). Devuelve objetos por fila con los campos reconocidos; una cabecera
 * desconocida se ignora (queda en `headers` con `field: null` para avisar).
 */
export function parseCsvTable<K extends string>(text: string, fields: Record<K, readonly string[]>, required: readonly NoInfer<K>[] = []): CsvTable<K> {
  const all = parseCsvRows(text);
  if (!all.length) return { headers: [], rows: [], missing: [...required] };
  const aliasToField = new Map<string, K>();
  for (const [field, aliases] of Object.entries(fields) as Array<[K, readonly string[]]>) {
    aliasToField.set(normHeader(field), field);
    for (const a of aliases) aliasToField.set(normHeader(a), field);
  }
  const headers = all[0].map((raw) => ({ raw, field: aliasToField.get(normHeader(raw)) ?? null }));
  const rows = all.slice(1).map((cells) => {
    const o: Partial<Record<K, string>> = {};
    headers.forEach((h, i) => {
      if (h.field && cells[i] !== undefined && cells[i] !== '') o[h.field] = cells[i];
    });
    return o;
  });
  const present = new Set(headers.map((h) => h.field).filter((f): f is K => !!f));
  return { headers, rows, missing: required.filter((r) => !present.has(r)) };
}

/** "a; b, c" → ['a','b','c'] (listas dentro de una celda). */
export function splitCsvList(cell: string | undefined): string[] {
  return (cell ?? '').split(/[;,|]/).map((s) => s.trim()).filter(Boolean);
}

/** "sí/si/yes/true/1/x" → true; vacío → undefined; el resto → false. */
export function csvBool(cell: string | undefined): boolean | undefined {
  if (cell === undefined || cell === '') return undefined;
  return /^(s[ií]|yes|y|true|1|x|verdadero)$/i.test(cell.trim());
}

/**
 * Número como lo escribe una planilla en Argentina o en inglés: "1.234,50" y "1,234.50" → 1234.5;
 * "12,5" → 12.5; un solo separador con exactamente 3 dígitos después ("1.234", "1,234") es de miles
 * → 1234; varios separadores iguales ("1.234.567") son de miles. Símbolos ($, espacios) se ignoran.
 * Vacío → undefined; inválido → NaN.
 */
export function csvNumber(cell: string | undefined): number | undefined {
  if (cell === undefined || cell.trim() === '') return undefined;
  const s = cell.trim().replace(/[^0-9,.\-]/g, '');
  if (!s) return NaN;
  const commas = (s.match(/,/g) ?? []).length;
  const dots = (s.match(/\./g) ?? []).length;
  let normalized: string;
  if (commas && dots) {
    normalized = s.lastIndexOf(',') > s.lastIndexOf('.') ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
  } else if (commas + dots === 1) {
    const sep = commas ? ',' : '.';
    const after = s.slice(s.lastIndexOf(sep) + 1);
    normalized = after.length === 3 ? s.replace(sep, '') : s.replace(',', '.');
  } else {
    normalized = s.replace(/[,.]/g, ''); // varios separadores iguales = miles
  }
  return Number(normalized);
}
